/**
 * Adapter hook: transforms DB-backed corporateEntries into the
 * Group[] / PrimaryEntity shapes that StructureMap & EntitiesDataTable expect.
 *
 * Key: the buildEntityTree algorithm needs:
 * 1. A "subsidiaries" group with subsidiary entities (they become child nodes)
 * 2. Groups with entityRef = subsidiary ACN to route officers/shareholders to the right node
 * 3. Groups without entityRef go to the primary entity node
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Doc, Id } from "@cvx/_generated/dataModel";
import type { PrimaryEntity, Group, EntitiesDataTableProps } from "../types";
import { isInsolvencyAppointeeDetail } from "@cvx/jobs/summarization/agents/corporate/insolvencyAppointees";

/**
 * `listCorporateEntriesForJob` still declares `returns: v.array(v.any())`,
 * but its handler returns `corporateEntries` rows unchanged — type the
 * boundary here as the stored document shape rather than editing the query.
 */
type CorporateEntry = Doc<"corporateEntries">;

const CATEGORY_LABELS: Record<string, string> = {
  officers: "Directors & Officers",
  shareholders: "Shareholders",
  related_companies: "Related Companies",
  subsidiaries: "Subsidiaries",
  trusts: "Related Trusts",
  secured_parties: "Secured Parties",
  professionals: "Professionals",
  other: "Other",
};

const PETITIONER_PATTERNS = [
  /\bpetitioner\b/i,
  /\bapplicant\b.*\bwinding[- ]?up\b/i,
  /\bwinding[- ]?up\b.*\bapplicant\b/i,
  /\bwinding[- ]?up\b.*\bapplication\b/i,
  /\bplaintiff\b.*\bliquidat/i,
  /\bliquidat.*\bpetition\b/i,
];

function isPetitioner(entry: {
  detail?: string | null;
  category?: string;
}): boolean {
  const { detail } = entry;
  if (!detail) return false;
  if (entry.category !== "related_companies" && entry.category !== "other")
    return false;
  return PETITIONER_PATTERNS.some((p) => p.test(detail));
}

export function useCorporateEntityData(jobId: Id<"jobs">) {
  const { data: entriesData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listCorporateEntriesForJob,
      { jobId, includesMerged: false },
    ),
  );
  // The query still declares `returns: v.array(v.any())`; type the boundary
  // once here as the stored document shape (see CorporateEntry above).
  const entries: CorporateEntry[] | undefined = entriesData;

  const result = useMemo(() => {
    if (!entries || entries.length === 0) {
      return {
        primaryEntity: null,
        groups: [],
        documentsAnalysed: 0,
        totalEntities: 0,
        displayEntityCount: 0,
      };
    }

    // Filter out:
    // 1. Entries flagged as excluded by the dedup agent (suppliers, vendors, etc.)
    // 2. Petitioners/applicants in court proceedings (creditors, not corporate structure)
    // 3. Professionals category (insolvency firms engaged for the appointment)
    // 4. External insolvency appointees (receivers, administrators, liquidators, controllers)
    const activeEntries = entries.filter(
      (e) =>
        !e.detail?.startsWith("[EXCLUDED") &&
        !isPetitioner(e) &&
        e.category !== "professionals" &&
        (e.isPrimaryEntity || !isInsolvencyAppointeeDetail(e.detail)),
    );

    // Find the primary entity
    const primaryEntry = activeEntries.find((e) => e.isPrimaryEntity);
    const primaryEntity: PrimaryEntity | null = primaryEntry
      ? {
          name: primaryEntry.entityName,
          acn: primaryEntry.acn ?? null,
          abn: primaryEntry.abn ?? null,
          status: primaryEntry.detail ?? null,
        }
      : null;

    // Group entries into Group[] for the structure map and entities table.
    //
    // Grouping key: category + entityRef
    // - Entries with no entityRef belong to the primary entity
    // - Entries with entityRef belong to that subsidiary/parent
    // - Subsidiaries with entityRef pointing to another subsidiary = nested structure
    const groupMap = new Map<string, CorporateEntry[]>();

    for (const entry of activeEntries) {
      // Skip the primary entity entry itself (shown via PrimaryEntity object)
      if (entry.isPrimaryEntity) continue;

      // entityRef determines which parent node this entry belongs to.
      // For subsidiaries: entityRef = ACN of parent (another subsidiary or empty for primary).
      // For other categories: entityRef = ACN of the entity they belong to.
      const ref = entry.entityRef ?? "";
      const key = `${entry.category}::${ref}`;
      const arr = groupMap.get(key) ?? [];
      arr.push(entry);
      groupMap.set(key, arr);
    }

    const groups: Group[] = [];
    for (const [key, groupEntries] of groupMap) {
      const sepIdx = key.indexOf("::");
      const category = key.slice(0, sepIdx);
      const ref = key.slice(sepIdx + 2);
      const firstEntry = groupEntries[0];
      const baseLabel = CATEGORY_LABELS[category] ?? category;

      const entityRef = ref || null;
      const entityName = entityRef ? (firstEntry.entityRefName ?? null) : null;

      // Suffix the label if scoped to a subsidiary
      const label = entityName ? `${baseLabel} (${entityName})` : baseLabel;

      groups.push({
        label,
        category,
        entityRef,
        entityName,
        entities: groupEntries.map((e) => ({
          name: e.entityName,
          type: e.entityType,
          detail: e.detail ?? null,
          acn: e.acn ?? null,
          abn: e.abn ?? null,
          isCurrent: e.isCurrent ?? null,
          sourceDocuments: (e.sourceDocuments ?? []).map((d) => ({
            id: d.documentId,
            fileName: d.fileName,
          })),
        })),
      });
    }

    // Sort: subsidiaries first (so buildEntityTree creates child nodes before
    // routing other groups), then officers, shareholders, etc.
    const categoryOrder = [
      "subsidiaries",
      "officers",
      "shareholders",
      "related_companies",
      "trusts",
      "secured_parties",
      "professionals",
      "other",
    ];
    groups.sort((a, b) => {
      const aIdx = categoryOrder.indexOf(a.category);
      const bIdx = categoryOrder.indexOf(b.category);
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    });

    // Count unique source documents
    const docIds = new Set<string>();
    for (const entry of activeEntries) {
      for (const doc of entry.sourceDocuments ?? []) {
        docIds.add(doc.documentId);
      }
    }

    // Count display entities: persons sharing the same name + parent are
    // merged into one row by EntitiesDataTable, so mirror that here.
    const personKeys = new Set<string>();
    const displayCount = groups.reduce(
      (groupCount, group) =>
        groupCount +
        group.entities.reduce((count, entity) => {
          if (entity.type !== "person") return count + 1;
          const key = `${entity.name.toLowerCase().trim()}::${group.entityRef ?? ""}`;
          if (personKeys.has(key)) return count;
          personKeys.add(key);
          return count + 1;
        }, 0),
      0,
    );

    return {
      primaryEntity,
      groups,
      documentsAnalysed: docIds.size,
      totalEntities: activeEntries.length,
      displayEntityCount: displayCount,
    };
  }, [entries]);

  // Build the output shape that EntitiesDataTable expects
  const output: EntitiesDataTableProps["output"] | null = useMemo(() => {
    if (!entries || entries.length === 0) return null;
    return {
      primaryEntity: result.primaryEntity,
      groups: result.groups,
      documentsAnalysed: result.documentsAnalysed,
    };
  }, [entries, result]);

  return {
    ...result,
    output,
    entries: entries ?? [],
    isLoading: entries === undefined,
  };
}
